import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCreativeLibrary, voteAsset, unvoteAsset, archiveAsset, unarchiveAsset, LibraryAsset } from '../services/marketingApi';
import { Kpis, Empty, Failed, Loading, Modal, PageFrame, Section, Tabs, Th, Thumb, btn, btnTone, input, td, trClick } from '../components/Kit';

/** Creative Library — every Meta ad creative as one row: its picture, shape,
 *  spend and conversations. Upvote or archive inline; a row opens the player. */

const POSTER_BASE = 'https://storage.googleapis.com/bhuvana-public/fortunegreens/marketing-assets/meta-ads-thumbs';

type Shape = 'all' | 'reel' | 'landscape' | 'square' | 'image';
type Dur = 'all' | 'short' | 'mid' | 'long';
type Acct = 'all' | 'group' | 'infra';
type Sort = 'votes' | 'spend' | 'conv' | 'cpc' | 'new' | 'reuse';

const rup = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN');

const parseTags = (a: LibraryAsset) => {
  const dims = a.tags.find((t) => /^\d+x\d+$/.test(t));
  const [w, h] = dims ? dims.split('x').map(Number) : [9, 16];
  const durTag = a.tags.find((t) => /^\d+s$/.test(t));
  const dur = durTag ? parseInt(durTag) : null;
  const shape: Exclude<Shape, 'all'> | 'vertical' =
    a.type === 'image' ? 'image'
    : a.tags.includes('reel') ? 'reel'
    : a.tags.includes('landscape') ? 'landscape'
    : a.tags.includes('square') ? 'square' : 'vertical';
  return { w, h, dur, shape };
};

const durBucket = (d: number | null): Dur | null => (d == null ? null : d <= 30 ? 'short' : d <= 60 ? 'mid' : 'long');

/** The poster frame (videos) or the image itself, in the kit's thumbnail tile. */
const CreativeThumb: React.FC<{ a: LibraryAsset }> = ({ a }) => (
  <span className="relative flex-none block">
    <Thumb src={a.type === 'video' ? `${POSTER_BASE}/${a.id}.jpg` : a.url} />
    {a.type === 'video' && <span className="absolute inset-0 grid place-items-center rounded-md text-warm-white text-[11px] bg-charcoal/25" aria-hidden>▶</span>}
  </span>
);

const meta = (a: LibraryAsset) => {
  const { w, h, dur } = parseTags(a);
  const tags = a.tags;
  const fmt = a.type === 'image' ? 'Image' : tags.includes('reel') ? 'Reel' : tags.includes('landscape') ? 'Landscape' : tags.includes('square') ? 'Square' : 'Video';
  const dims = tags.some((t) => /^\d+x\d+$/.test(t)) ? `${w}×${h}` : null;
  return [fmt, dur != null ? `${dur}s` : null, dims].filter(Boolean).join(' · ');
};
const acctName = (a: LibraryAsset) => (a.account_id?.endsWith('2487') ? 'Bhuvana Group' : 'Bhuvana Infra');

const CreativeLibrary: React.FC = () => {
  const { user } = useAuth();
  const voter = user?.email || '';
  const [assets, setAssets] = useState<LibraryAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [shape, setShape] = useState<Shape>('all');
  const [dur, setDur] = useState<Dur>('all');
  const [acct, setAcct] = useState<Acct>('all');
  const [sort, setSort] = useState<Sort>('spend');
  const [mineOnly, setMineOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState<LibraryAsset | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getCreativeLibrary(voter)
      .then((r) => setAssets(r.assets))
      .catch((err) => setError(err?.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [voter]);

  const toggleVote = async (asset: LibraryAsset) => {
    const call = asset.voted_by_me ? unvoteAsset : voteAsset;
    const res = await call(asset.id, voter);
    const patch = { vote_count: res.vote_count, voted_by_me: res.voted_by_me };
    setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, ...patch } : a)));
    setOpen((prev) => (prev && prev.id === asset.id ? { ...prev, ...patch } : prev));
  };

  const toggleArchive = async (asset: LibraryAsset) => {
    const res = asset.archived ? await unarchiveAsset(asset.id) : await archiveAsset(asset.id);
    setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, archived: res.archived } : a)));
    setOpen((prev) => (prev && prev.id === asset.id ? { ...prev, archived: res.archived } : prev));
  };

  const rows = useMemo(() => {
    const filteredRows = assets.filter((a) => {
      const t = parseTags(a);
      return (
        a.archived === showArchived &&
        (!mineOnly || a.voted_by_me) &&
        (shape === 'all' || (shape === 'image' ? a.type === 'image' : t.shape === shape && a.type === 'video')) &&
        (dur === 'all' || durBucket(t.dur) === dur) &&
        (acct === 'all' || (acct === 'group') === Boolean(a.account_id?.endsWith('2487')))
      );
    });
    const key: Record<Sort, (a: LibraryAsset) => number> = {
      votes: (a) => -a.vote_count,
      spend: (a) => -a.spend,
      conv: (a) => -a.conversations,
      cpc: (a) => (a.conversations ? a.spend / a.conversations : Infinity),
      new: (a) => -(a.first_used ? new Date(a.first_used).getTime() : 0),
      reuse: (a) => -a.ads_count,
    };
    return filteredRows.sort((x, y) => key[sort](x) - key[sort](y) || y.spend - x.spend);
  }, [assets, shape, dur, acct, sort, mineOnly, showArchived]);

  const live = useMemo(() => assets.filter((a) => !a.archived), [assets]);
  const archivedCount = assets.length - live.length;
  const myVotes = live.filter((a) => a.voted_by_me).length;
  const stats = useMemo(() => [
    [live.length, 'unique creatives'],
    [live.filter((a) => a.tags.includes('reel')).length, 'reels'],
    [rup(live.reduce((s, a) => s + a.spend, 0)), 'spend behind them'],
    [live.reduce((s, a) => s + a.conversations, 0).toLocaleString('en-IN'), 'WhatsApp conversations'],
  ] as const, [live]);

  const rowBtn = `${btn} ${btnTone.ghost} h-7 px-2 text-[12px]`;
  const closeOpen = () => setOpen(null);

  const bar = (
    <>
      <Tabs value={shape} onChange={setShape}
            items={[['all', 'All'], ['reel', 'Reels'], ['landscape', 'Landscape'], ['square', 'Square'], ['image', 'Images']]} />
      <Tabs value={dur} onChange={setDur}
            items={[['all', 'Any length'], ['short', '< 30s'], ['mid', '30–60s'], ['long', '> 60s']]} />
      <Tabs value={acct} onChange={setAcct}
            items={[['all', 'Both accounts'], ['group', 'Bhuvana Group'], ['infra', 'Bhuvana Infra']]} />
      <Tabs value={`${mineOnly ? 'mine' : ''}${showArchived ? 'arch' : ''}` || 'all'}
            onChange={(k) => { setMineOnly(k === 'mine'); setShowArchived(k === 'arch'); }}
            items={[['all', 'Library'], ['mine', `▲ My upvotes${myVotes ? ` (${myVotes})` : ''}`], ['arch', `Archived${archivedCount ? ` (${archivedCount})` : ''}`]]} />
      <span className="flex-1" />
      <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} className={`${input} h-8`} aria-label="Sort creatives">
        <option value="votes">Most upvoted</option>
        <option value="spend">Highest spend</option>
        <option value="conv">Most conversations</option>
        <option value="cpc">Best ₹ / conversation</option>
        <option value="new">Newest first</option>
        <option value="reuse">Most reused</option>
      </select>
    </>
  );

  return (
    <PageFrame title="Creative Library" subline="Every ad creative from both Meta accounts · synced nightly" bar={bar}>
      {error ? <Failed what="the creative library" error={error} /> : loading ? <Loading what="creatives" /> : (
        <>
          <Kpis items={stats} />

          <Section title={`${rows.length} of ${showArchived ? archivedCount : live.length} creatives`}
                   note={`${rup(rows.reduce((s, a) => s + a.spend, 0))} spend`}>
            <table className="w-full min-w-[980px] border-collapse">
              <thead><tr>
                <Th left>Creative</Th><Th left>Account</Th><Th>Votes</Th><Th>Spend</Th><Th>Conversations</Th>
                <Th>₹ / conv</Th><Th>Ads</Th><Th>First used</Th>
                <th className="w-[1%] border-b border-charcoal/10" />
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={9}>No creatives match these filters.</Empty>}
                {rows.map((a) => (
                  <tr key={a.id} onClick={() => setOpen(a)} className={`${trClick()} group`}>
                    <td className="px-3 py-2.5 max-w-[420px]">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <CreativeThumb a={a} />
                        <div className="min-w-0">
                          <div className="font-semibold text-[14px] text-charcoal group-hover:text-forest truncate">{a.name}</div>
                          <div className="text-[12px] text-graphite mt-0.5">{meta(a)}</div>
                        </div>
                      </div>
                    </td>
                    <td className={`${td} text-[13px] text-graphite`}>{acctName(a)}</td>
                    <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                      <button type="button" onClick={() => toggleVote(a)} aria-pressed={a.voted_by_me}
                              title={a.voted_by_me ? 'Remove your upvote' : 'Upvote'}
                              className={`inline-flex h-7 min-w-[40px] items-center justify-center gap-1 rounded-full px-2.5 text-[12px] font-semibold num transition-colors ${
                                a.voted_by_me ? 'bg-gold/20 text-amber-ink' : 'text-graphite hover:bg-charcoal/5 hover:text-charcoal'}`}>
                        ▲{a.vote_count ? ` ${a.vote_count}` : ''}
                      </button>
                    </td>
                    <td className={`${td} text-right font-semibold text-[14px]`}>{a.spend ? rup(a.spend) : <span className="text-graphite font-normal">—</span>}</td>
                    <td className={`${td} text-right`}>{a.conversations ? <span className="text-forest font-semibold">{a.conversations.toLocaleString('en-IN')}</span> : <span className="text-graphite">—</span>}</td>
                    <td className={`${td} text-right text-graphite`}>{a.conversations ? rup(a.spend / a.conversations) : '—'}</td>
                    <td className={`${td} text-right text-graphite`}>{a.ads_count}</td>
                    <td className={`${td} text-right text-[13px] text-graphite`}>{a.first_used || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className={`flex justify-end transition-opacity ${a.archived ? '' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`}>
                        <button type="button" onClick={() => toggleArchive(a)} className={rowBtn}
                                title={a.archived ? 'Restore to library' : 'Archive — hide from library'}>
                          {a.archived ? '↺ Restore' : 'Archive'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}

      {open && (
        <Modal
          wide
          title={open.name}
          onClose={closeOpen}
          footer={
            <>
              <button type="button" onClick={() => toggleArchive(open)} className={`${btn} ${btnTone.ghost} mr-auto`}>
                {open.archived ? '↺ Restore to library' : 'Archive'}
              </button>
              <button type="button" onClick={() => toggleVote(open)} aria-pressed={open.voted_by_me}
                      className={`${btn} ${open.voted_by_me ? 'bg-gold/20 text-amber-ink hover:bg-gold/30' : btnTone.charcoal}`}>
                {open.voted_by_me ? `▲ Upvoted (${open.vote_count}) — remove` : `▲ Upvote${open.vote_count ? ` (${open.vote_count})` : ''}`}
              </button>
              <a href={open.url} target="_blank" rel="noopener noreferrer" className={`${btn} ${btnTone.forest}`}>Open full file</a>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(220px,320px)_1fr]">
            <div className="grid place-items-center rounded-lg bg-charcoal overflow-hidden self-start">
              {open.type === 'video' ? (
                <video src={open.url} poster={`${POSTER_BASE}/${open.id}.jpg`} controls autoPlay className="max-h-[70vh] w-full object-contain" />
              ) : (
                <img src={open.url} alt={open.name} className="max-h-[70vh] w-full object-contain" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] text-graphite">
                {acctName(open)} · {meta(open)}{open.first_used ? ` · first used ${open.first_used}` : ''}
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-3 mt-4">
                {([
                  [rup(open.spend), 'spend'],
                  [open.impressions.toLocaleString('en-IN'), 'impressions'],
                  [open.conversations.toLocaleString('en-IN'), 'conversations'],
                  [open.conversations ? rup(open.spend / open.conversations) : '—', '₹ / conversation'],
                  [String(open.ads_count), 'ads used it'],
                ] as const).map(([v, l]) => (
                  <div key={l}>
                    <div className="text-[18px] font-semibold num text-charcoal leading-none">{v}</div>
                    <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-graphite mt-1">{l}</div>
                  </div>
                ))}
              </div>
              {open.tags.filter((t) => t !== 'meta-ads').length > 0 && (
                <div className="mt-4 text-[12px] text-graphite">{open.tags.filter((t) => t !== 'meta-ads').join(' · ')}</div>
              )}
              {open.copy && (open.copy.title || open.copy.body) && (
                <div className="mt-5 border-l-2 border-gold pl-3.5">
                  {open.copy.title && <div className="font-semibold text-charcoal">{open.copy.title}</div>}
                  {open.copy.body && <div className="whitespace-pre-wrap text-[13.5px] text-graphite mt-0.5">{open.copy.body}</div>}
                </div>
              )}
              {open.ads.length > 0 && (
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-[0.06em] font-semibold text-graphite pb-2 border-b border-charcoal/15">Ads using it</div>
                  {open.ads.map((ad, i) => (
                    <div key={i} className="flex justify-between gap-3 border-b border-charcoal/[0.06] py-2.5 text-[13px] last:border-b-0">
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-charcoal">{ad.name}</span>
                        <span className="text-[12px] text-graphite">
                          {ad.campaign}{ad.objective ? ` · ${ad.objective.replace('OUTCOME_', '').toLowerCase()}` : ''}
                        </span>
                      </span>
                      <span className="whitespace-nowrap num text-charcoal">{ad.spend ? rup(ad.spend) : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </PageFrame>
  );
};

export default CreativeLibrary;
